/**
 * Định dạng hiển thị dùng chung — tiền, số, ngày giờ theo locale i18n (vi-VN mặc định, en-US khi đổi ngôn ngữ). Không dùng để tính toán.
 * T6-A (29/09/2026, quyết định #5/#6 — theo mockup v152):
 *  - vi: ngày `DD/MM/YYYY`, ngày giờ `HH:mm DD/MM/YYYY` (đệm 0), tiền `12.500.000 ₫` (ký hiệu SAU số, cách một khoảng không ngắt dòng);
 *  - en: giữ định dạng en-US (`MM/DD/YYYY`, `MM/DD/YYYY, HH:mm`, `₫12,500,000`);
 *  - ngày/giờ quy về MÚI GIỜ TENANT (`setTenantTimeZone`, mặc định Asia/Ho_Chi_Minh) — không theo máy người xem;
 *    chuỗi ngày thuần `YYYY-MM-DD` (ngày nghiệp vụ, cột DATE) giữ nguyên ngày, KHÔNG đổi múi giờ.
 */
import { currentLocale, intlTag, type Locale } from './i18n';

const dash = '—';
const NBSP = ' ';

/* ---------- múi giờ tenant ---------- */
export const DEFAULT_TENANT_TIME_ZONE = 'Asia/Ho_Chi_Minh';
let tenantZone = DEFAULT_TENANT_TIME_ZONE;
const zoneListeners = new Set<(tz: string) => void>();

/** Múi giờ IANA hợp lệ với Intl của trình duyệt? */
export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** Múi giờ tenant đang dùng cho ngày nghiệp vụ và hiển thị giờ. */
export const tenantTimeZone = (): string => tenantZone;

/** Đặt múi giờ tenant (từ hồ sơ tenant/phiên). Giá trị không hợp lệ/rỗng → về mặc định. Trả về múi giờ đã áp. */
export function setTenantTimeZone(tz: string | null | undefined): string {
  const next = isValidTimeZone(tz) ? tz : DEFAULT_TENANT_TIME_ZONE;
  if (next !== tenantZone) {
    tenantZone = next;
    dtfCache.clear();
    for (const l of zoneListeners) l(next);
  }
  return tenantZone;
}

/** Theo dõi đổi múi giờ (React: `useSyncExternalStore`). */
export function subscribeTenantTimeZone(listener: (tz: string) => void): () => void {
  zoneListeners.add(listener);
  return () => {
    zoneListeners.delete(listener);
  };
}

/* ---------- số / tiền ---------- */
const nfCache = new Map<string, Intl.NumberFormat>();
function nf(maxFraction: number, locale: Locale, minFraction = 0): Intl.NumberFormat {
  const k = `${maxFraction}:${minFraction}:${locale}`;
  let f = nfCache.get(k);
  if (!f) {
    f = new Intl.NumberFormat(intlTag(locale), {
      maximumFractionDigits: maxFraction,
      minimumFractionDigits: minFraction,
    });
    nfCache.set(k, f);
  }
  return f;
}
const toNum = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
};

/** Ký hiệu tiền tệ theo mã ISO (VND → ₫). Chuỗi không phải mã ISO 3 chữ (vd '₫', 'đ') được coi là ký hiệu. */
const CURRENCY_SYMBOL: Record<string, string> = {
  VND: '₫',
  USD: '$',
  EUR: '€',
  JPY: '¥',
  CNY: '¥',
  KRW: '₩',
  THB: '฿',
  SGD: 'S$',
  GBP: '£',
};
const CURRENCY_DECIMALS: Record<string, number> = { VND: 0, JPY: 0, KRW: 0 };
function currencyOf(currency: string | null | undefined): { symbol: string; decimals: number } {
  const c = (currency ?? '').trim();
  if (!c || c === '₫' || c.toLowerCase() === 'đ') return { symbol: '₫', decimals: 0 };
  if (/^[A-Za-z]{3}$/.test(c)) {
    const iso = c.toUpperCase();
    return { symbol: CURRENCY_SYMBOL[iso] ?? iso, decimals: CURRENCY_DECIMALS[iso] ?? 2 };
  }
  return { symbol: c, decimals: 0 };
}

/**
 * Tiền: vi `12.500.000 ₫` · en `₫12,500,000`. `currency` nhận mã ISO (`VND`, `USD`) hoặc ký hiệu (`₫`); trống = ₫.
 * VND không lẻ; ngoại tệ 2 chữ số lẻ.
 */
export function money(v: number | string | null | undefined, currency: string | null = '₫'): string {
  const n = toNum(v);
  if (n === null) return dash;
  const { symbol, decimals } = currencyOf(currency);
  const loc = currentLocale();
  const body = nf(decimals, loc, decimals > 0 ? Math.min(2, decimals) : 0).format(Math.abs(n));
  const sign = n < 0 ? '-' : '';
  if (loc === 'en') return `${sign}${symbol}${symbol.length > 1 ? NBSP : ''}${body}`;
  return `${sign}${body}${NBSP}${symbol}`;
}

/**
 * 13/10/2026 (💰 người dùng chốt "credit note gồm VAT, tách rõ hai phần"): hiện tiền tách VAT — `150.000 ₫ + VAT 12.000 ₫ = 162.000 ₫`;
 * không có VAT (0/null) → chỉ số gồm VAT (= chưa VAT). Dùng cho phiếu trả, RMA, credit note.
 */
export function moneyVat(
  net: number | string | null | undefined,
  tax: number | string | null | undefined,
  gross: number | string | null | undefined,
  currency: string | null = '₫',
): string {
  const t = toNum(tax);
  if (t === null || t === 0) return money(gross ?? net, currency);
  return `${money(net, currency)} + VAT ${money(t, currency)} = ${money(gross, currency)}`;
}

/** Rút gọn kiểu mockup v152 (KPI): ₫1,84 tỷ · ₫62,8M · ₫450K (en: ₫1.84B · ₫62.8M). */
export function moneyShort(v: number | string | null | undefined): string {
  const n = toNum(v);
  if (n === null) return dash;
  const loc = currentLocale();
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  const NUM = nf(2, loc);
  const [val, unit] =
    abs >= 1e9
      ? [abs / 1e9, loc === 'en' ? 'B' : ' tỷ']
      : abs >= 1e6
        ? [abs / 1e6, 'M']
        : abs >= 1e3
          ? [abs / 1e3, 'K']
          : [abs, ''];
  const body = unit ? `${NUM.format(val)}${unit}` : nf(0, loc).format(val);
  return `${sign}₫${body}`;
}

/** Số theo locale (tối đa `maxFraction` chữ số lẻ, mặc định 2) — thay mọi `toFixed` khi HIỂN THỊ. */
export function num(v: number | string | null | undefined, maxFraction = 2): string {
  const n = toNum(v);
  return n === null ? dash : nf(maxFraction, currentLocale()).format(n);
}

/**
 * KOVIX-2651 — ô NHẬP số thập phân theo locale (vi: dấu phẩy thập phân, dấu chấm phân cách nghìn — cùng kiểu `num()` hiển thị bên cạnh).
 * `<input type="number">` hiện dấu thập phân theo ngôn ngữ trình duyệt nên cùng màn hình lẫn "1.2346" (ô nhập) với "1,2346" (cột hiển thị).
 * Giá trị máy (state, API) luôn dấu chấm. Chữ gõ MƠ HỒ (vi "1.234" — nghìn hay thập phân?) bị TỪ CHỐI kèm gợi ý, không đoán: giá trị máy
 * khi đó mang tiền tố {@link DECIMAL_INPUT_INVALID} (Number() → NaN nên mọi kiểm "> 0" đều chặn) và {@link decimalInputError} trả câu lỗi đúng.
 */
export function decimalSeparator(locale: Locale = currentLocale()): string {
  return (
    nf(1, locale)
      .formatToParts(1.5)
      .find((p) => p.type === 'decimal')?.value ?? '.'
  );
}

/** Tiền tố giá trị máy của chữ nhập không hiểu được (phần sau tiền tố = chữ người gõ, để hiện lại nguyên văn). */
export const DECIMAL_INPUT_INVALID = '\u0000';

const reEsc = (c: string) => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Chữ người gõ → giá trị máy (dấu chấm) hoặc lỗi. Theo locale (vi: thập phân `,` · nghìn `.`; en ngược lại). Bỏ khoảng trắng; rỗng → `''`.
 *  - chỉ dấu thập phân (một lần): `1,5` → `1.5`;
 *  - có cả nghìn và thập phân đúng nhóm 3: `1.234,5` → `1234.5`;
 *  - chỉ dấu nghìn đúng nhóm 3 (`1.234`, `1.234.567`): MƠ HỒ → lỗi "dùng dấu phẩy cho phần thập phân" (tránh hiểu 1.234 kg thành 1,234 kg hay ngược lại);
 *  - một dấu nghìn với số chữ số sau ≠ 3 (`1.5`, `2.25`): thói quen bàn phím số → thập phân;
 *  - còn lại (ký tự lạ, nhiều dấu sai nhóm) → lỗi định dạng. Dấu `-` giữ nguyên để lớp kiểm nghiệp vụ báo "> 0".
 */
export function parseDecimalInput(
  text: string,
  locale: Locale = currentLocale(),
): { value: string; error: null } | { value: null; error: string } {
  const t = text.replace(/\s/g, '');
  if (t === '') return { value: '', error: null };
  const d = decimalSeparator(locale);
  const g = d === ',' ? '.' : ',';
  const D = reEsc(d);
  const G = reEsc(g);
  const vi = locale !== 'en';
  const bad = vi
    ? `Số không hợp lệ — dấu chấm phân cách nghìn, dấu phẩy phần thập phân (vd 1.234,5)`
    : `Invalid number — use "," for thousands and "." for decimals (e.g. 1,234.5)`;
  if (!new RegExp(`^-?[\\d${D}${G}]+$`).test(t)) return { value: null, error: bad };
  const hasD = t.includes(d);
  const hasG = t.includes(g);
  if (hasD && hasG) {
    if (new RegExp(`^-?\\d{1,3}(${G}\\d{3})+${D}\\d*$`).test(t))
      return { value: t.split(g).join('').replace(d, '.'), error: null };
    return { value: null, error: bad };
  }
  if (hasG) {
    if (new RegExp(`^-?\\d{1,3}(${G}\\d{3})+$`).test(t))
      return {
        value: null,
        error: vi
          ? `Dùng dấu phẩy cho phần thập phân (vd 1,234); không cần nhập dấu chấm phân cách nghìn`
          : `Use "." for decimals (e.g. 1.234); do not type thousands separators`,
      };
    if (new RegExp(`^-?\\d*${G}\\d*$`).test(t)) return { value: t.replace(g, '.'), error: null };
    return { value: null, error: bad };
  }
  if (hasD) {
    if (new RegExp(`^-?\\d*${D}\\d*$`).test(t)) return { value: t.replace(d, '.'), error: null };
    return { value: null, error: bad };
  }
  return { value: t, error: null };
}

/** Chữ người gõ → giá trị máy: số hợp lệ dấu chấm; không hiểu được → `DECIMAL_INPUT_INVALID + chữ gõ`. */
export function decimalInputValue(text: string): string {
  const r = parseDecimalInput(text);
  return r.error === null ? r.value : DECIMAL_INPUT_INVALID + text;
}

/** Lỗi định dạng của giá trị máy do {@link decimalInputValue} sinh (null = không phải lỗi định dạng). */
export function decimalInputError(v: string | null | undefined): string | null {
  if (!v?.startsWith(DECIMAL_INPUT_INVALID)) return null;
  return parseDecimalInput(v.slice(DECIMAL_INPUT_INVALID.length)).error;
}

/** Giá trị máy (`'1234.5'`) → chữ trong ô nhập theo locale (vi `1234,5`); giá trị lỗi định dạng → chữ người đã gõ. */
export function decimalInputText(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (s.startsWith(DECIMAL_INPUT_INVALID)) return s.slice(DECIMAL_INPUT_INVALID.length);
  const sep = decimalSeparator();
  return sep === '.' ? s : s.replaceAll('.', sep);
}

/** Phần trăm từ số đã nhân 100 (`12.3` → vi `12,3 %`, en `12.3%`). Mặc định tối đa 2 chữ số lẻ (lãi suất 0,05 % không bị làm tròn mất). */
export function pct(v: number | string | null | undefined, maxFraction = 2): string {
  const n = toNum(v);
  if (n === null) return dash;
  return currentLocale() === 'en' ? `${num(n, maxFraction)}%` : `${num(n, maxFraction)}${NBSP}%`;
}

/** Phần trăm từ tỷ lệ 0–1 (`0.123` → `12,3 %`). */
export function ratioPct(v: number | string | null | undefined, maxFraction = 1): string {
  const n = toNum(v);
  return n === null ? dash : pct(n * 100, maxFraction);
}

/* ---------- ngày giờ ---------- */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const dtfCache = new Map<string, Intl.DateTimeFormat>();
function parts(d: Date, withTime: boolean): Record<string, string> {
  const k = `${withTime ? 't' : 'd'}:${tenantZone}`;
  let f = dtfCache.get(k);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: tenantZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(withTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' as const } : {}),
    });
    dtfCache.set(k, f);
  }
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(d)) if (p.type !== 'literal') out[p.type] = p.value;
  return out;
}
function toDate(v: string | Date): Date | null {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
const fmtDate = (y: string, m: string, d: string, loc: Locale) => (loc === 'en' ? `${m}/${d}/${y}` : `${d}/${m}/${y}`);

/** Ngày: vi `DD/MM/YYYY`, en `MM/DD/YYYY`. Thời điểm (ISO có giờ) quy về múi giờ tenant; `YYYY-MM-DD` giữ nguyên. */
export function date(v: string | Date | null | undefined): string {
  if (!v) return dash;
  const loc = currentLocale();
  if (typeof v === 'string') {
    const m = DATE_ONLY.exec(v);
    if (m) return fmtDate(m[1] as string, m[2] as string, m[3] as string, loc);
  }
  const d = toDate(v);
  if (!d) return typeof v === 'string' ? v : dash;
  const p = parts(d, false);
  return fmtDate(p.year ?? '', p.month ?? '', p.day ?? '', loc);
}

/** Ngày giờ: vi `HH:mm DD/MM/YYYY`, en `MM/DD/YYYY, HH:mm` — theo múi giờ tenant. Chuỗi ngày thuần → như `date`. */
export function dateTime(v: string | Date | null | undefined): string {
  if (!v) return dash;
  if (typeof v === 'string' && DATE_ONLY.test(v)) return date(v);
  const d = toDate(v);
  if (!d) return typeof v === 'string' ? v : dash;
  const p = parts(d, true);
  const hm = `${p.hour ?? '00'}:${p.minute ?? '00'}`;
  const loc = currentLocale();
  const dd = fmtDate(p.year ?? '', p.month ?? '', p.day ?? '', loc);
  return loc === 'en' ? `${dd}, ${hm}` : `${hm} ${dd}`;
}

/** Giờ `HH:mm` theo múi giờ tenant. */
export function time(v: string | Date | null | undefined): string {
  if (!v) return dash;
  const d = toDate(v);
  if (!d) return typeof v === 'string' ? v : dash;
  const p = parts(d, true);
  return `${p.hour ?? '00'}:${p.minute ?? '00'}`;
}

export function shortId(v: string | null | undefined): string {
  return v ? `${v.slice(0, 8)}…` : dash;
}
